import { AlertCircle, Clock, LogOut01 } from "@untitledui/icons";
import { useEffect, useId, useRef } from "react";
import { Heading as AriaHeading } from "react-aria-components";
import {
	Dialog,
	DialogTrigger,
	Modal,
	ModalOverlay,
} from "@/components/application/modals/modal";
import { Avatar } from "@/components/base/avatar/avatar";
import { Button } from "@/components/base/buttons/button";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { formatTimeRemaining } from "@/hooks/use-inactivity-timeout";

/**
 * Props for the InactivityWarningModal component
 */
export interface InactivityWarningModalProps {
	/** Whether the modal is open/visible */
	isOpen: boolean;
	/** Time remaining before automatic logout (in milliseconds) */
	timeRemaining: number;
	/** Callback when user chooses to stay logged in */
	onStayLoggedIn: () => void;
	/** Callback when user chooses to log out immediately */
	onLogout: () => void;
	/** Whether to auto-focus the "Stay Logged In" button */
	autoFocus?: boolean;
}

/**
 * Modal component that warns users about session timeout
 *
 * Features:
 * - Real-time countdown display
 * - Two action buttons (Stay Signed In / Sign Out Now)
 * - Accessibility support with ARIA announcements
 * - Auto-focus management
 * - Keyboard navigation support
 * - Visual warning indicators
 * - i18n translations support
 */
export function InactivityWarningModal({
	isOpen,
	timeRemaining,
	onStayLoggedIn,
	onLogout,
	autoFocus = true,
}: InactivityWarningModalProps) {
	const { t } = useAppTranslation();
	// Generate unique IDs for accessibility
	const titleId = useId();
	const descriptionId = useId();

	// Format the remaining time for display
	const formattedTime = formatTimeRemaining(timeRemaining);

	// Determine urgency level for styling
	const isUrgent = timeRemaining <= 60000; // Less than 1 minute
	const isCritical = timeRemaining <= 30000; // Less than 30 seconds

	return (
		<DialogTrigger isOpen={isOpen}>
			<ModalOverlay isDismissable={false}>
				<Modal>
					<Dialog
						role="alertdialog"
						aria-labelledby={titleId}
						aria-describedby={descriptionId}
					>
						<div className="relative w-full overflow-hidden rounded-2xl bg-primary shadow-xl sm:max-w-md">
							{/* Header with Icon */}
							<div className="flex flex-col gap-4 px-4 pt-5 sm:px-6 sm:pt-6">
								<div className="relative w-max mx-auto">
									<Avatar
										size="2xl"
										placeholderIcon={AlertCircle}
										className={
											isCritical
												? "!bg-error-secondary"
												: "!bg-warning-secondary"
										}
									/>
								</div>

								{/* Title */}
								<div className="z-10 flex flex-col gap-1 text-center">
									<AriaHeading
										id={titleId}
										slot="title"
										className="text-lg font-semibold text-primary"
									>
										{t("inactivity.modal.title")}
									</AriaHeading>

									{/* Primary Message */}
									<p className="text-sm text-tertiary">
										{t("inactivity.modal.message")}
									</p>
								</div>

								{/* Countdown Timer */}
								<div className="flex justify-center">
									<div
										className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg font-mono text-lg font-semibold ${
											isCritical
												? "bg-error-secondary text-error-primary"
												: isUrgent
													? "bg-warning-secondary text-warning-primary"
													: "bg-brand-secondary text-brand-primary"
										}`}
										aria-live="polite"
										aria-atomic="true"
									>
										<Clock className="size-5" aria-hidden="true" />
										<span>
											{formattedTime}
											<span className="sr-only">
												{" "}
												{t("inactivity.modal.timeRemaining")}
											</span>
										</span>
									</div>
								</div>

								{/* Secondary Warning Message */}
								<p className="text-sm text-tertiary text-center">
									{t("inactivity.modal.warningMessage")}
								</p>

								{/* Urgency indicator */}
								{isUrgent && (
									<p
										className={`text-sm font-medium text-center ${
											isCritical ? "text-error-primary" : "text-warning-primary"
										}`}
										aria-live="assertive"
									>
										{isCritical
											? t("inactivity.modal.loggingOutSoon")
											: t("inactivity.modal.timeRunningOut")}
									</p>
								)}
							</div>

							{/* Action Buttons */}
							<div className="pt-4 sm:pt-6">
								<div className="z-10 flex flex-1 flex-col-reverse gap-3 border-t border-secondary p-4 *:grow sm:grid sm:grid-cols-2 sm:p-6">
									<Button
										color="primary"
										size="lg"
										onClick={onStayLoggedIn}
										autoFocus={autoFocus}
									>
										{t("inactivity.modal.staySignedIn")}
									</Button>
									<Button
										color="secondary"
										size="lg"
										onClick={onLogout}
										iconLeading={LogOut01}
									>
										{t("inactivity.modal.signOutNow")}
									</Button>
								</div>
							</div>

							{/* Screen Reader Instructions */}
							<div id={descriptionId} className="sr-only" aria-live="polite">
								{t("inactivity.modal.title")}. {t("inactivity.modal.message")}{" "}
								{formattedTime} {t("inactivity.modal.timeRemaining")}.
							</div>
						</div>
					</Dialog>
				</Modal>
			</ModalOverlay>
		</DialogTrigger>
	);
}

/**
 * Hook to provide accessibility announcements for the inactivity modal
 * Can be used to announce countdown milestones to screen readers
 */
export function useInactivityAnnouncements(
	timeRemaining: number,
	isWarning: boolean,
) {
	const { t } = useAppTranslation();
	const announcementRef = useRef<HTMLDivElement>(null);
	const lastAnnouncementRef = useRef<number>(0);

	useEffect(() => {
		if (!isWarning || !announcementRef.current) return;

		const minutes = Math.ceil(timeRemaining / 60000);
		const shouldAnnounce =
			minutes <= 5 && // Only announce in final 5 minutes
			minutes !== lastAnnouncementRef.current && // Don't repeat same minute
			timeRemaining % 60000 < 1000; // Announce at the top of each minute

		if (shouldAnnounce) {
			lastAnnouncementRef.current = minutes;
			const announcement =
				minutes === 1
					? t("inactivity.announcements.oneMinute")
					: t("inactivity.announcements.minutes", { count: minutes });

			// Update the announcement element
			if (announcementRef.current) {
				announcementRef.current.textContent = announcement;
			}
		}
	}, [timeRemaining, isWarning, t]);

	// Return the announcement element to be rendered
	return (
		<div
			ref={announcementRef}
			className="sr-only"
			aria-live="polite"
			aria-atomic="true"
		/>
	);
}
